import React, { DependencyList, useEffect, useState } from 'react';
import {
    Box,
    IconButton,
    Pagination,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getNumber } from '../../../../helpers';
import { ChevronLeftIcon, ChevronRightIcon } from '../icon';

// Fixed width (px) of the paginator's slot from md up. Comfortably fits the
// widest numbered pagination with siblingCount=1 (first/last + ellipses ≈ 9
// buttons) without letting the slot resize as the page count changes.
const PAGINATOR_MD_WIDTH = 380;

interface IUsePaginator {
    KEY: string;
    take?: number;
}

export const usePaginator = function (
    { KEY, take: takeProp }: IUsePaginator,
    deps: DependencyList = [],
) {
    const [take] = useState(takeProp ?? (KEY ? getNumber(`${KEY}_TAKE_KEY`) || 10 : 10));
    const [skip, setSkip] = useState(0);

    useEffect(() => {
        setSkip(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [KEY, ...deps]);

    return {
        take,
        skip,
        setSkip,
    };
};

interface ICustomPaginatorProps {
    count?: number;
    skip: number;
    setSkip: (skip: number) => void;
    take: number;
    disableGutters?: boolean;
}

export function DPaginator({
    count = 0,
    take,
    skip,
    setSkip,
    disableGutters = false,
}: ICustomPaginatorProps) {
    const pages = Math.floor(count / take) + (count % take === 0 ? 0 : 1);
    const currentPage = Math.floor(skip / take) + 1;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    // From md up the paginator occupies a FIXED-width, right-aligned slot. The
    // numbered pagination's width otherwise grows/shrinks with the page count,
    // which would keep resizing whatever sits beside it (the general filter).
    // Reserving a stable slot keeps that neighbor's width constant. Below md the
    // slot collapses to natural width and re-centers (compact stepper).
    const wrapperSx = {
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-end' },
        alignItems: 'center',
        width: { xs: 'auto', md: PAGINATOR_MD_WIDTH },
        flexShrink: 0,
        mb: !disableGutters ? 2 : 0,
    };

    // On medium and below the full numbered pagination gets wide (first/last
    // pages + ellipses), so collapse it to a compact "current / total" stepper
    // that stays a fixed, small width.
    if (isSmallScreen) {
        return (
            <Box sx={{ ...wrapperSx, gap: 0.5 }}>
                <IconButton
                    size={'small'}
                    aria-label={'Página anterior'}
                    disabled={currentPage <= 1}
                    onClick={() => setSkip((currentPage - 2) * take)}
                >
                    <ChevronLeftIcon fontSize={'small'} />
                </IconButton>
                <Typography
                    variant={'body2'}
                    sx={{ minWidth: 48, textAlign: 'center', userSelect: 'none' }}
                >
                    {currentPage} / {pages || 1}
                </Typography>
                <IconButton
                    size={'small'}
                    aria-label={'Página siguiente'}
                    disabled={currentPage >= pages}
                    onClick={() => setSkip(currentPage * take)}
                >
                    <ChevronRightIcon fontSize={'small'} />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box sx={wrapperSx}>
            <Pagination
                count={pages}
                page={currentPage}
                siblingCount={1}
                onChange={(
                    event: React.ChangeEvent<unknown>,
                    value: number,
                ) => {
                    event.preventDefault();
                    setSkip((value - 1) * take);
                }}
            />
        </Box>
    );
}
